/**
 * Types for the API-wrapper (apigen) section. These mirror the backend
 * `/v1/models` breakdown and the shared call response.
 */

export type ApiInputType = 'image' | 'mask' | 'text' | 'number' | 'images' | 'select';

export interface ApiInputSpec {
  name: string;
  type: ApiInputType;
  required: boolean;
  label: string;
  /** Allowed values for a `select` input. */
  options?: string[];
  /** Default value (used for `select` inputs). */
  default?: string | null;
}

/**
 * The quality preset a merch run resolves and stamps on every gen-api call it
 * makes. Each tiered endpoint carries one independent parameter setup per tier.
 * Kept in step with the merch vocabulary, tier0 included, so a run on any tier
 * can name it here.
 */
export type GenerationTier = 'tier0' | 'tier1' | 'tier2' | 'tier3' | 'tier4';

export const GENERATION_TIERS: GenerationTier[] = ['tier0', 'tier1', 'tier2', 'tier3', 'tier4'];

/** What a request that names no tier gets — the pre-tier production preset. */
export const DEFAULT_GENERATION_TIER: GenerationTier = 'tier4';

/** One tunable knob of a tier setup, as described by the backend. */
export interface TierFieldSpec {
  name: string;
  label: string;
  type: 'text' | 'select' | 'number';
  options?: string[];
  help: string;
}

/**
 * One tier's setup. Only the fields it actually overrides are present; anything
 * absent falls back to the endpoint's own default.
 */
export type TierOverride = Record<string, string | number>;

/** An endpoint's four tier setups plus the defaults they fall back to. */
export interface ModelTierConfig {
  model_key: string;
  default_tier: GenerationTier;
  tier_fields: TierFieldSpec[];
  /** What each field resolves to when a tier leaves it blank. */
  defaults: Record<string, string | number | null>;
  tiers: Record<GenerationTier, TierOverride>;
  /** True when setups have been saved for this endpoint (vs. the shipped defaults). */
  overridden: boolean;
}

export interface ApiEndpointModel {
  key: string;
  label: string;
  description: string;
  path: string;
  /** Provider declared in `models.yaml`. */
  provider: string;
  /**
   * Provider tier that would actually serve a request with the backend's
   * current env (differs from `provider` where a fallback chain applies, e.g.
   * gpt-image-2 running on Azure).
   */
  active_provider?: string;
  model_id: string | null;
  endpoint: string | null;
  pricing: Record<string, number>;
  inputs: ApiInputSpec[];
  /**
   * Whether this endpoint resolves a per-tier setup. False for `erase` and
   * `region_op`, which have nothing a tier could change. Absent when talking to
   * a backend that predates generation tiers.
   */
  tiered?: boolean;
  default_tier?: GenerationTier;
  /** The knobs a tier may override here; empty on untiered endpoints. */
  tier_fields?: TierFieldSpec[];
  /** Each tier's effective setup (shipped defaults with saved overrides applied). */
  tiers?: Record<GenerationTier, TierOverride>;
}

export interface ApiModelsResponse {
  models: ApiEndpointModel[];
}

/** Liveness payload from the apigen service's own `/health` endpoint. */
export interface ApigenHealth {
  status: string;
  version: string;
  models: string[];
  /**
   * Service-wide dummy mode: provider calls are replaced by a sleep plus a
   * noise image, so nothing is bought. Individual calls can also opt in via the
   * `X-Apigen-Dummy` header, which this flag does not reflect.
   */
  dummy_mode?: boolean;
}

export interface ApiCallResponse {
  record_id: string;
  status: string;
  model_type: string;
  model_id: string | null;
  provider: string | null;
  output_image: string | null;
  output_url: string | null;
  cost_usd: number | null;
  elapsed_ms: number | null;
  error: string | null;
}

/**
 * One recorded API call, mirroring the backend `api_calls` row
 * (`ApiCallModel.to_dict()`). Drives the per-endpoint Request History.
 */
export interface ApiCallRecord {
  id: string;
  created_at: string | null;
  scan_id: string | null;
  organization_id: string | null;
  job_id: string | null;
  view: string | null;
  player: string | null;
  dvp: string | null;
  /**
   * How the call was triggered: `manual` (run from the Playground), `stress`
   * (generated by a stress test) or `api` (any other client). Null on rows
   * recorded before this was tracked.
   */
  source: string | null;
  /** True when the provider call was replaced by a dummy, so the row cost nothing. */
  dummy?: boolean | null;
  /** The stress test that generated this call, if any. */
  stress_test_id?: string | null;
  model_type: string;
  model_id: string | null;
  provider: string | null;
  /**
   * Which of the endpoint's four parameter setups served this call. Null on
   * untiered endpoints and on rows recorded before tiers existed.
   */
  generation_tier?: GenerationTier | null;
  prompt: string | null;
  /**
   * The system instruction added to salvage the call after the provider's
   * non-configurable filter rejected the input. `prompt` above is what the
   * caller sent, unchanged. Null on every ordinary call, so a non-null value is
   * exactly the signal "this output needed a safety retry".
   */
  retry_system_instruction?: string | null;
  seed: number | null;
  params: Record<string, unknown> | null;
  input_refs: Record<string, string> | null;
  output_ref: string | null;
  cost_usd: number | null;
  duration_seconds: number | null;
  /**
   * Transient provider failures retried before the call settled, summed over
   * every provider tier it walked. Null while a call is still pending, and on
   * rows recorded before retries were tracked.
   */
  retries?: number | null;
  status: string;
  error: string | null;
  /**
   * The provider-run bracket. `created_at` is when the request was accepted, so
   * the wait before `started_at` is queue + container boot; `duration_seconds`
   * covers execution only. Both are null while a call is still in flight.
   */
  started_at?: string | null;
  finished_at?: string | null;
  /** End-to-end wall time: request accepted to provider finished. */
  total_seconds?: number | null;
}

export interface ApiCallsResponse {
  calls: ApiCallRecord[];
}

/**
 * One scan/job that sent calls to gen-api (`GET /v1/traces`), summarised for
 * the trace browse list. Either id can be empty: a caller stamping only one of
 * the two still gets a traceable group, keyed by whichever it sent.
 */
export interface ApiTraceRow {
  scan_id: string;
  job_id: string;
  calls: number;
  errors: number;
  cost_usd: number;
  /** Distinct non-empty views the trace's calls covered. */
  views: string[];
  player: string | null;
  organization_id: string | null;
  first_at: string | null;
  last_at: string | null;
}

export interface ApiTracesResponse {
  from: string;
  to: string;
  traces: ApiTraceRow[];
}

/**
 * Server-side aggregated statistics for a time window (`GET /v1/stats`).
 * Fixed-size regardless of how many calls fall in the window.
 */
export interface ApiStatsTotals {
  total: number;
  success: number;
  errors: number;
  cost_usd: number;
  avg_cost_usd: number;
  /** Distinct job ids (scans) seen in the window. */
  scans: number;
  /** Calls carrying a job id, divided by distinct job ids. */
  avg_calls_per_scan: number;
  /** Execution-duration percentiles, in seconds. */
  p50: number | null;
  p75: number | null;
  p90: number | null;
  p95: number | null;
  p99: number | null;
  /** Queue-wait percentiles, in seconds. */
  queue_p50: number | null;
  queue_p75: number | null;
  queue_p90: number | null;
  queue_p95: number | null;
  queue_p99: number | null;
  /** Container-boot percentiles (cold starts only), in seconds. */
  boot_p50: number | null;
  boot_p75: number | null;
  boot_p90: number | null;
  boot_p95: number | null;
  boot_p99: number | null;
  /** Total startup (queue + boot) percentiles, in seconds. */
  startup_p50: number | null;
  startup_p75: number | null;
  startup_p90: number | null;
  startup_p95: number | null;
  startup_p99: number | null;
  cold_boots: number;
  /** Share of timed calls that had to wait for a container to come up (0-1). */
  cold_boot_rate: number;
}

export interface ApiStatsSeriesPoint {
  /** Bucket start (ISO-8601). */
  ts: string | null;
  success: number;
  error: number;
  /** Summed cost of the calls in this bucket (USD). */
  cost_usd: number;
  /** Execution duration percentiles within the bucket, in seconds. */
  exec_p50: number | null;
  exec_p75: number | null;
  exec_p90: number | null;
  /** Startup (queue + boot) percentiles within the bucket, in seconds. */
  startup_p50: number | null;
  startup_p75: number | null;
  startup_p90: number | null;
  /** The two halves of startup, for the breakdown view. In seconds. */
  queue_p90: number | null;
  boot_p90: number | null;
}

/** One provider tier's slice of some aggregate — the vendor that billed us. */
export interface ApiProviderCostRow {
  provider: string;
  cost_usd: number;
  calls: number;
}

/** One `job_id`'s aggregated gen-api spend (`GET /v1/stats/by-job`). */
export interface ApiJobCostRow {
  job_id: string;
  cost_usd: number;
  calls: number;
  /** Absent when talking to a backend that predates the per-provider split. */
  by_provider?: ApiProviderCostRow[];
}

export interface ApiJobCostResponse {
  from: string;
  to: string;
  by_job: ApiJobCostRow[];
}

/** One endpoint's slice of a player's traffic, with its share of their calls (0-1). */
export interface ApiPlayerModelRow {
  model_type: string;
  calls: number;
  cost_usd: number;
  share: number;
}

/**
 * One player's usage (`GET /v1/stats/by-player`). `player` is the email of
 * whoever initiated the calls, and is empty for calls that carried none.
 *
 * The per-job averages divide the player's job-carrying calls (`job_calls`) and
 * their cost (`job_cost_usd`) by the distinct jobs among them, so Playground and
 * ad-hoc calls sit in `calls`/`cost_usd` without diluting either average.
 */
export interface ApiPlayerStatsRow {
  player: string;
  calls: number;
  errors: number;
  jobs: number;
  job_calls: number;
  avg_calls_per_job: number;
  cost_usd: number;
  job_cost_usd: number;
  avg_cost_per_job: number;
  by_model: ApiPlayerModelRow[];
}

/**
 * Window-wide totals accompanying the per-player rows. `players` counts named
 * players only, so calls with no player are excluded from it even though they
 * appear as a row of their own.
 */
export interface ApiPlayerStatsTotals {
  players: number;
  calls: number;
  errors: number;
  jobs: number;
  job_calls: number;
  avg_calls_per_job: number;
  cost_usd: number;
  job_cost_usd: number;
  avg_cost_per_job: number;
}

export interface ApiPlayerStatsResponse {
  from: string;
  to: string;
  totals: ApiPlayerStatsTotals;
  by_player: ApiPlayerStatsRow[];
  /** The window's endpoint split across every player. */
  by_model: ApiPlayerModelRow[];
}

export interface ApiStatsModelRow {
  model_type: string;
  total: number;
  errors: number;
  cost_usd: number;
}

/**
 * Spend grouped by the provider tier that actually served each call, which is
 * the vendor billing us for it. Drives the Cost section's vendor breakdown.
 */
export interface ApiStatsProviderRow {
  provider: string;
  total: number;
  errors: number;
  cost_usd: number;
}

/**
 * Row-level slice shared by the recent-errors and slowest-requests tables.
 * `duration_seconds` is execution only; `total_seconds` is end-to-end
 * (accepted -> finished) and is null for calls recorded before that was tracked.
 */
export interface ApiStatsCallRow {
  id: string;
  created_at: string | null;
  model_type: string | null;
  status: string | null;
  error: string | null;
  duration_seconds: number | null;
  total_seconds: number | null;
}

/**
 * How many calls needed a given number of retries. Only calls whose retry count
 * is known are counted, so the shares describe measured traffic rather than
 * being diluted by rows that predate the tracking.
 */
export interface ApiRetryGroup {
  retries: number;
  count: number;
}

export interface ApiStatsResponse {
  from: string;
  to: string;
  bucket_seconds: number;
  totals: ApiStatsTotals;
  series: ApiStatsSeriesPoint[];
  by_model: ApiStatsModelRow[];
  /** Absent when talking to a backend that predates the per-provider split. */
  by_provider?: ApiStatsProviderRow[];
  /** Absent when talking to a backend that predates retry tracking. */
  retry_groups?: ApiRetryGroup[];
  recent_errors: ApiStatsCallRow[];
  slowest: ApiStatsCallRow[];
}

// ---------------------------------------------------------------------------
// Stress tests
// ---------------------------------------------------------------------------

/** A run in one of these states will never change again — stop polling it. */
export const STRESS_TERMINAL_STATUSES = ['completed', 'cancelled', 'error'] as const;

export type StressTestStatus = 'pending' | 'running' | 'completed' | 'cancelled' | 'error';

export function isStressTerminal(status: string): boolean {
  return (STRESS_TERMINAL_STATUSES as readonly string[]).includes(status);
}

/** The knobs a stress run is configured with, as stored on its row. */
export interface StressTestConfig {
  /** An endpoint key, or `random` to spread the run across all of them. */
  model_type: string;
  total_calls: number;
  burst_size: number;
  burst_delay_seconds: number;
  /** Synchronous submits. Forced on when S3 isn't configured. */
  wait: boolean;
  image_width: number;
  image_height: number;
  prompt: string;
  trigger_lightning_batch: boolean;
}

/** Progress of the lightning batch a stress run kicked off. */
export interface StressLightningBatch {
  batch_id: string;
  filename: string;
  total_scans: number;
  completed: number;
  failed: number;
  /** Only present while the batch is still active. */
  in_progress?: number;
  pending?: number;
  completion_percentage?: number;
  success_percentage: number;
  created_at: string;
  /** False while the batch is still in lightning's active list. */
  finished: boolean;
}

/** One stress test, mirroring the backend `stress_tests` row. */
export interface StressTest {
  id: string;
  created_at: string | null;
  name: string | null;
  status: StressTestStatus;
  config: StressTestConfig;
  scan_ids: string[];
  filename: string | null;
  lightning_batch_id: string | null;
  lightning_total_scans: number | null;
  /** Why the lightning batch couldn't be started; the gen-api half still ran. */
  lightning_error: string | null;
  calls_planned: number;
  calls_submitted: number;
  calls_failed_to_submit: number;
  bursts_completed: number;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  /** Live batch progress, only on the single-test endpoint. */
  lightning_batch?: StressLightningBatch | null;
}

export interface StressTestListResponse {
  tests: StressTest[];
}

/** Static options the submit form needs (`GET /v1/stress/config`). */
export interface StressOptions {
  /** Every selectable value, including the randomized sentinel. */
  model_types: string[];
  /** The sentinel within `model_types` that means "spread across all endpoints". */
  random_model_type: string;
  default_model_type: string;
  default_prompt: string;
  max_total_calls: number;
  max_burst_size: number;
  max_burst_delay_seconds: number;
  max_image_edge: number;
  dummy_mode: boolean;
  lightning_configured: boolean;
  async_submits_supported: boolean;
}

export interface StressTestCreateRequest {
  name?: string;
  filename?: string;
  scan_ids: string[];
  model_type: string;
  total_calls: number;
  burst_size: number;
  burst_delay_seconds: number;
  image_width: number;
  image_height: number;
  prompt?: string;
  trigger_lightning_batch: boolean;
}

export interface StressTestCreatedResponse {
  id: string;
  status: StressTestStatus;
  bursts: number;
  estimated_duration_seconds: number;
  lightning_batch_requested: boolean;
  /** The configuration as it will actually run, after clamping and fallbacks. */
  config: StressTestConfig;
  warnings: string[];
}

/**
 * Aggregates over every gen-api call a stress test produced. Execution, queue
 * wait and end-to-end time are reported separately: under load the telling
 * signal is queueing growing while execution stays flat.
 */
export interface StressAnalyticsTotals {
  total: number;
  success: number;
  errors: number;
  /** Still pending or processing. */
  in_flight: number;
  cost_usd: number;
  cold_boots: number;
  first_call_at: string | null;
  last_call_at: string | null;
  span_seconds: number | null;
  throughput_per_min: number | null;
  exec_p50: number | null;
  exec_p90: number | null;
  exec_p95: number | null;
  exec_p99: number | null;
  queue_p50: number | null;
  queue_p90: number | null;
  queue_p95: number | null;
  queue_p99: number | null;
  total_p50: number | null;
  total_p90: number | null;
  total_p95: number | null;
  total_p99: number | null;
}

export interface StressAnalyticsSeriesPoint {
  ts: string | null;
  submitted: number;
  success: number;
  error: number;
  exec_p50: number | null;
  exec_p90: number | null;
  queue_p50: number | null;
  queue_p90: number | null;
}

export interface StressErrorGroup {
  error: string;
  count: number;
}

/** One endpoint's share of a run — the point of a randomized test. */
export interface StressModelGroup {
  model_type: string;
  total: number;
  success: number;
  errors: number;
  exec_p50: number | null;
  exec_p90: number | null;
}

export interface StressAnalytics {
  stress_test_id: string;
  bucket_seconds: number;
  totals: StressAnalyticsTotals;
  series: StressAnalyticsSeriesPoint[];
  by_model: StressModelGroup[];
  /** Absent when talking to a backend that predates retry tracking. */
  retry_groups?: ApiRetryGroup[];
  error_groups: StressErrorGroup[];
  slowest: ApiStatsCallRow[];
}
